import { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Alert, AlertDescription } from "../components/ui/alert";
import { ArrowLeft, ArrowRight, Loader2, AlertTriangle, CheckCircle2, Sparkles, Shield, Brain, Clock, TrendingUp, Info } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { sendGA4, GA4 } from "../hooks/useAnalytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LegalBanner() {
  const { t } = useTranslation();
  return (
    <div className="bg-slate-800 text-slate-300 text-xs py-2 px-4 text-center" data-testid="legal-banner">
      <Info className="inline h-3 w-3 mr-1.5 -mt-0.5" />
      {t("legal.banner")}
    </div>
  );
}

export default function WizardPage() {
  const { formId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.split("-")[0] || "en";
  const [wizard, setWizard] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [answer, setAnswer] = useState("");

  useEffect(() => {
    const init = async () => {
      try {
        const existingSession = searchParams.get("session");
        if (existingSession) {
          const res = await axios.get(`${API}/wizard/${existingSession}`, { params: { locale }, withCredentials: true });
          setWizard(res.data);
          setForm(res.data.form);
        } else {
          const res = await axios.post(`${API}/wizard/start`, { form_id: formId }, { withCredentials: true });
          setWizard(res.data);
          const formRes = await axios.get(`${API}/forms/${formId}`, { params: { locale } });
          setForm(formRes.data);
        }
      } catch (err) {
        toast.error("Failed to start wizard");
        navigate("/forms");
      }
      setLoading(false);
    };
    init();
  }, [formId, searchParams, navigate, locale]);

  const currentField = form?.fields?.[wizard?.current_step];
  const progress = wizard ? (wizard.current_step / wizard.total_steps) * 100 : 0;
  const isCompleted = wizard?.status === "completed";

  const handleSubmitAnswer = async () => {
    if (!answer.trim() && currentField?.required) {
      toast.error(t("pages.thisFieldRequired"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/wizard/${wizard.session_id}/answer`,
        { field_id: currentField.field_id, answer: answer.trim() },
        { withCredentials: true }
      );
      setWizard(res.data);
      setAnswer("");
      if (res.data?.status === "completed") {
        sendGA4(GA4.GUIDE_COMPLETED, { form_id: formId, form_name: res.data.form_name });
        sendGA4(GA4.FORM_COMPLETE, { form_id: formId, form_name: res.data.form_name });
      }
    } catch (err) {
      toast.error("Failed to save answer");
    }
    setSubmitting(false);
  };

  const handleCheckErrors = async () => {
    setChecking(true);
    try {
      const res = await axios.post(
        `${API}/wizard/${wizard.session_id}/check-errors`,
        {},
        { withCredentials: true }
      );
      const errors = res.data.errors || [];
      if (errors.length === 0) {
        toast.success(t("pages.noErrorsDetected"));
      } else {
        setWizard((prev) => ({ ...prev, errors }));
        toast.warning(`${errors.length} ${t("pages.foundIssues")}`);
      }
    } catch {
      toast.error("Failed to check errors");
    }
    setChecking(false);
  };

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const res = await axios.post(
        `${API}/wizard/${wizard.session_id}/analyze`,
        {},
        { withCredentials: true }
      );
      setAnalysis(res.data.analysis);
      toast.success("Analysis complete!");
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error(t("pages.proFeature"));
      } else {
        toast.error("Failed to analyze");
      }
    }
    setAnalyzing(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="wizard-loading">
        <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  if (isCompleted) {
    return (
      <main className="min-h-screen bg-background" data-testid="wizard-completed">
        <LegalBanner />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 font-heading mb-3">{t("pages.wizardComplete")}</h1>
          <p className="text-base text-slate-600 mb-8">
            {t("pages.wizardCompleteDesc")} {wizard.form_name}.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
            <Button
              onClick={handleCheckErrors}
              disabled={checking}
              className="bg-teal-700 hover:bg-teal-800 text-white font-medium"
              data-testid="check-errors-btn"
            >
              {checking ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
              {t("pages.checkForErrors")}
            </Button>
            <Button
              onClick={handleAnalyze}
              disabled={analyzing}
              variant="outline"
              className="border-violet-300 text-violet-700 hover:bg-violet-50"
              data-testid="analyze-btn"
            >
              {analyzing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Brain className="h-4 w-4 mr-2" />}
              {t("pages.aiAnalysis")}
            </Button>
            <Button variant="outline" onClick={() => navigate(`/checklists`)} data-testid="view-checklist-btn">
              {t("pages.generateDocChecklist")}
            </Button>
          </div>

          {/* AI Analysis Panel */}
          {analysis && (
            <Card className="border-violet-200 bg-violet-50/30 mb-6 text-left" data-testid="ai-analysis-panel">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Brain className="h-5 w-5 text-violet-700" />
                  <h3 className="text-base font-semibold text-slate-900">{t("pages.aiAnalysisInsights")}</h3>
                  {analysis.confidence_score && (
                    <Badge className="bg-violet-100 text-violet-700 text-xs ml-auto">
                      {t("pages.confidence")} {analysis.confidence_score}%
                    </Badge>
                  )}
                </div>

                {analysis.overall_assessment && (
                  <p className="text-sm text-slate-700 mb-4 bg-white p-3 rounded-lg border border-violet-100">
                    {analysis.overall_assessment}
                  </p>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {analysis.estimated_processing_time && (
                    <div className="flex items-start gap-2">
                      <Clock className="h-4 w-4 text-slate-500 mt-0.5" />
                      <div>
                        <p className="text-xs font-medium text-slate-500">{t("pages.estProcessingTime")}</p>
                        <p className="text-sm font-semibold text-slate-900">{analysis.estimated_processing_time}</p>
                      </div>
                    </div>
                  )}
                  {analysis.risk_level && (
                    <div className="flex items-start gap-2">
                      <TrendingUp className="h-4 w-4 text-slate-500 mt-0.5" />
                      <div>
                        <p className="text-xs font-medium text-slate-500">{t("pages.riskLevel")}</p>
                        <Badge className={`text-xs ${
                          analysis.risk_level === "low" ? "bg-emerald-100 text-emerald-700" :
                          analysis.risk_level === "high" ? "bg-red-100 text-red-700" :
                          "bg-amber-100 text-amber-700"
                        }`}>{analysis.risk_level}</Badge>
                      </div>
                    </div>
                  )}
                </div>

                {analysis.potential_issues?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-red-700 mb-2">{t("pages.potentialIssues")}</p>
                    <ul className="space-y-1.5">
                      {analysis.potential_issues.map((issue, i) => (
                        <li key={i} className="text-sm text-red-600 flex items-start gap-2">
                          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> {issue}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {analysis.recommendations?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-teal-700 mb-2">{t("pages.recommendations")}</p>
                    <ul className="space-y-1.5">
                      {analysis.recommendations.map((rec, i) => (
                        <li key={i} className="text-sm text-teal-600 flex items-start gap-2">
                          <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {analysis.next_steps?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">{t("pages.nextSteps")}</p>
                    <ol className="space-y-1.5">
                      {analysis.next_steps.map((step, i) => (
                        <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                          <span className="text-xs font-bold text-slate-400 mt-0.5">{i+1}.</span> {step}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          {wizard.errors?.length > 0 && (
            <div className="space-y-3 text-left">
              {wizard.errors.map((err, i) => (
                <Alert key={i} variant={err.severity === "error" ? "destructive" : "default"} className={err.severity === "warning" ? "border-amber-200 bg-amber-50" : err.severity === "info" ? "border-sky-200 bg-sky-50" : ""}>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    <strong className="capitalize">{err.severity}:</strong> {err.message}
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          )}
          <div className="mt-8">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard">
              <ArrowLeft className="mr-2 h-4 w-4" /> {t("pages.backToDashboard")}
            </Button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background" data-testid="wizard-page">
      <LegalBanner />
      <div className="max-w-2xl mx-auto px-4 py-8 md:py-12">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/forms")} data-testid="wizard-back">
              <ArrowLeft className="mr-1 h-3.5 w-3.5" /> {t("pages.exitWizard")}
            </Button>
            <span className="text-sm font-medium text-slate-500">
              {t("dashboard.step")} {wizard.current_step + 1} {t("dashboard.of")} {wizard.total_steps}
            </span>
          </div>
          <div className="wizard-progress-track">
            <div className="wizard-progress-fill" style={{ width: `${progress}%` }} data-testid="wizard-progress" />
          </div>
        </div>

        {/* Current field */}
        {currentField && (
          <Card className="border-slate-200 shadow-sm" data-testid="wizard-current-field">
            <CardContent className="p-6 md:p-8">
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Badge variant="outline" className="text-xs">{currentField.section}</Badge>
                  {currentField.required && <Badge className="bg-red-100 text-red-700 text-xs">{t("pages.fieldRequired")}</Badge>}
                </div>
                <h2 className="text-2xl font-semibold text-slate-900 font-heading mb-2">
                  {currentField.label}
                </h2>
                <p className="text-base text-slate-600">{currentField.description}</p>
              </div>

              {/* Tip */}
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-2">
                  <Shield className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-teal-800">{currentField.help_text}</p>
                </div>
              </div>

              {/* Input */}
              <div className="mb-6">
                <Label className="text-sm font-medium text-slate-700 mb-2 block">{t("pages.yourAnswer")}</Label>
                {currentField.field_type === "select" && currentField.options ? (
                  <Select value={answer} onValueChange={setAnswer}>
                    <SelectTrigger className="h-12 border-slate-200" data-testid="wizard-select-input">
                      <SelectValue placeholder={t("pages.selectOption")} />
                    </SelectTrigger>
                    <SelectContent>
                      {currentField.options.map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type={currentField.field_type === "date" ? "date" : "text"}
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder={t("pages.typeAnswer")}
                    className="h-12 border-slate-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                    data-testid="wizard-text-input"
                  />
                )}
              </div>

              {/* Common mistakes warning */}
              {currentField.common_mistakes?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">{t("pages.watchOut")}</span>
                  </div>
                  <ul className="space-y-1">
                    {currentField.common_mistakes.map((m, i) => (
                      <li key={i} className="text-sm text-amber-700">- {m}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between items-center pt-4 border-t border-slate-100">
                <Button
                  variant="ghost"
                  disabled={wizard.current_step === 0}
                  className="text-slate-600"
                  data-testid="wizard-skip"
                  onClick={() => {
                    setAnswer("");
                    handleSubmitAnswer();
                  }}
                >
                  {t("pages.skip")}
                </Button>
                <Button
                  onClick={handleSubmitAnswer}
                  disabled={submitting}
                  className="bg-teal-700 hover:bg-teal-800 text-white font-medium px-8"
                  data-testid="wizard-next-btn"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {wizard.current_step === wizard.total_steps - 1 ? t("pages.finish") : t("pages.next")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </main>
  );
}
